import { useEffect, useRef, useState, type ReactElement } from "react";
import { PANEL_LINE } from "../theme.js";
import { ModPreview } from "./modPreview.js";

/**
 * A loose part, turning: the Inspect dialog's picture of an attachment. A
 * part's tile is already a render from ONE angle, and the player wants the
 * other side — so the dialog mounts the same `ModPreview`, pointed at one part
 * (`setPart`). It costs a GL context while open, which is why tiles do not do
 * this too. Headless, the box is simply empty.
 */

/**
 * The viewport, in pixels — one box for every part, NOT the item's footprint:
 * `ModPreview` fits by bounding SPHERE, bound by the SHORT side, so a 4×1
 * barrel in a 4:1 letterbox would draw at a quarter width. Fixed and nearly
 * square; the footprint is stated in the row below. Slightly inside the 300 px
 * column so the border reads as a frame.
 */
const PART_VIEW_W = 280;
const PART_VIEW_H = 200;

export function PartTurntable(props: { defId: string }): ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<ModPreview | null>(null);
  const orbitPointer = useRef<{ pointerId: number; x: number; y: number } | null>(null);
  const [orbiting, setOrbiting] = useState(false);

  // The canvas is NOT in this JSX (see `modPreview.ts`): a released context
  // poisons the element, and StrictMode mounts twice.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const made = new ModPreview(host, PART_VIEW_W, PART_VIEW_H);
    previewRef.current = made;
    made.start();
    return () => {
      previewRef.current = null;
      // Renderer, GL context, canvas, materials and lights, all of it.
      made.dispose();
    };
  }, []);

  // Declared after the mount effect, so the preview is always there to be told
  // what to draw — including on the very first render.
  useEffect(() => {
    previewRef.current?.setPart(props.defId);
  }, [props.defId]);

  const endOrbit = (pointerId: number): void => {
    if (orbitPointer.current?.pointerId !== pointerId) return;
    orbitPointer.current = null;
    setOrbiting(false);
  };

  return (
    <div
      ref={hostRef}
      onPointerDown={(ev) => {
        if (ev.button !== 0) return;
        // The dialog's own `Panel` drags the WINDOW by anything pressed inside
        // it that does not claim the press first. A drag here is the model's.
        ev.preventDefault();
        ev.stopPropagation();
        ev.currentTarget.setPointerCapture(ev.pointerId);
        orbitPointer.current = { pointerId: ev.pointerId, x: ev.clientX, y: ev.clientY };
        setOrbiting(true);
      }}
      onPointerMove={(ev) => {
        const active = orbitPointer.current;
        if (active === null || active.pointerId !== ev.pointerId) return;
        const dx = ev.clientX - active.x;
        const dy = ev.clientY - active.y;
        active.x = ev.clientX;
        active.y = ev.clientY;
        previewRef.current?.rotateBy(dx, dy);
      }}
      onPointerUp={(ev) => endOrbit(ev.pointerId)}
      onPointerCancel={(ev) => endOrbit(ev.pointerId)}
      onLostPointerCapture={(ev) => endOrbit(ev.pointerId)}
      style={{
        // The same hatched, bordered viewport the Werkbank turns a whole gun
        // in — one look for "this is a model you can rotate", wherever it is.
        position: "relative",
        width: PART_VIEW_W,
        height: PART_VIEW_H,
        margin: "0 auto",
        borderRadius: 4,
        border: `1px solid ${PANEL_LINE}`,
        background:
          "repeating-linear-gradient(135deg, rgba(255,255,255,.015) 0 3px, transparent 3px 9px), #080a0b",
        overflow: "hidden",
        cursor: orbiting ? "grabbing" : "grab",
        touchAction: "none",
        userSelect: "none",
      }}
    />
  );
}
